import React, { Suspense, useMemo } from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'
import { csx } from '@vtex/admin-ui'

import { ProductPromotionHighlight } from '../../../core/components'
import { useProductPageUrlParams } from '../hooks'
import type { ProductSkuHighlightedPromotion_Product$key } from './__generated__/ProductSkuHighlightedPromotion_Product.graphql'

const productSkuFragment = graphql`
  fragment ProductSkuHighlightedPromotion_Product on Product {
    skus {
      edges {
        node {
          id
          promotions(addresses: $addresses) {
            edges {
              node {
                id
                name
                highlight
              }
            }
          }
        }
      }
    }
  }
`

type ProductSkuHighlightedPromotionContentProps = {
  productRef: ProductSkuHighlightedPromotion_Product$key
}

const ProductSkuHighlightedPromotionContent = ({
  productRef,
}: ProductSkuHighlightedPromotionContentProps) => {
  const { selectedSkuId } = useProductPageUrlParams()
  const product = useSuspenseFragment(productSkuFragment, productRef)

  const selectedSku = useMemo(
    () => product.skus.edges.find((sku) => sku.node.id === selectedSkuId),
    [product.skus.edges, selectedSkuId]
  )

  // TODO: create util inline fragment
  const promotions = useMemo(() => {
    return selectedSku?.node.promotions?.edges.map((promotionEdge) => ({
      id: promotionEdge.node.id,
      name: promotionEdge.node.name,
      highlight: !!promotionEdge.node.highlight,
    }))
  }, [selectedSku?.node.promotions?.edges])

  if (!promotions?.length) {
    return null
  }

  return (
    <ProductPromotionHighlight
      promotions={promotions}
      className={csx({ zIndex: 1 })}
    />
  )
}

export type ProductSkuHighlightedPromotionProps = {
  productRef: ProductSkuHighlightedPromotion_Product$key
}

export const ProductSkuHighlightedPromotion = (
  props: ProductSkuHighlightedPromotionProps
) => (
  <Suspense fallback={null}>
    <ProductSkuHighlightedPromotionContent {...props} />
  </Suspense>
)
